// Tabs.jsx

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// CSS
import './Tabs.css';

// Components
import Tab from './tab/Tab';
import SelectInput from 'components/selectinput/SelectInput';

/**
 * Tabs
 *
 * Container for tab components.
 * - layout="row" (default): single-line tabs; collapses to SelectInput on overflow.
 * - layout="wrap": wrapping chip grid; all tabs stay visible (no SelectInput).
 *
 * @param {object[]} tabs - Array of tab objects with {key, label, icon?, iconKey?, iconResource?, description?, onClick, rightContent?}
 * @param {string} selectedKey - The key of the currently selected tab
 * @param {boolean} showLabels - Whether to show tab labels (if false, only icons are shown)
 * @param {Number} tabWidth - (Optional) Fixed width for each tab
 * @param {'row'|'wrap'} layout - Tab layout mode
 * @param {ReactNode} endSlot - Optional content rendered after the tabs (e.g. add button in wrap layout)
 *
 * @returns {ReactNode} - Tabs.
 */
const Tabs = React.memo(({ tabs, selectedKey, showLabels, tabWidth, layout = 'row', endSlot = null }) => {

  // ----- REFS ----- //

  const wrapperRef = useRef(null);

  const measureRef = useRef(null);
  const containerRef = useRef(null);
  const tabRefs = useRef(new Map());

  // ----- STATE ----- //

  const [useSelectFallback, setUseSelectFallback] = useState(false);

  // ----- REFS ----- //

  const layoutModeRef = useRef(false);

  // ----- STATE ----- //

  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0, opacity: 0 });

  // ----- MEMOS ----- //

  const isWrapLayout = layout === 'wrap';

  const selectOptions = useMemo(() => (
    (Array.isArray(tabs) ? tabs : []).map((tab) => ({
      value: tab.key,
      label: tab.label,
      iconKey: tab.iconKey || '',
      iconResource: tab.iconResource || '',
      note: tab.description || tab.note || '',
    }))
  ), [tabs]);

  const updateLayoutMode = useCallback(() => {
    if (isWrapLayout) {
      layoutModeRef.current = false;
      setUseSelectFallback(false);
      return;
    }

    if (!wrapperRef.current || !measureRef.current) return;

    const availableWidth = wrapperRef.current.clientWidth;
    const requiredWidth = measureRef.current.scrollWidth;
    const overflowThreshold = 1;
    const collapseThreshold = 24;

    if (requiredWidth > availableWidth + overflowThreshold) {
      layoutModeRef.current = true;
    } else if (availableWidth > requiredWidth + collapseThreshold) {
      layoutModeRef.current = false;
    }

    setUseSelectFallback(layoutModeRef.current);
  }, [isWrapLayout]);

  const updateIndicator = useCallback(() => {
    if (isWrapLayout || useSelectFallback || !containerRef.current || !selectedKey) {
      setIndicatorStyle((prev) => (prev.opacity === 0 ? prev : { ...prev, opacity: 0 }));
      return;
    }

    const activeTab = tabRefs.current.get(selectedKey);
    if (!activeTab) {
      setIndicatorStyle((prev) => (prev.opacity === 0 ? prev : { ...prev, opacity: 0 }));
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const labelEl = activeTab.querySelector('.tab-label');
    const targetRect = labelEl
      ? labelEl.getBoundingClientRect()
      : (activeTab.querySelector('.tab-body') ?? activeTab).getBoundingClientRect();
    const indicatorWidth = (targetRect.width) * 0.8;
    const centeredLeft = (targetRect.left - containerRect.left) + ((targetRect.width - indicatorWidth) / 2);

    setIndicatorStyle((prev) => {
      if (
        prev.opacity === 1
        && prev.left === centeredLeft
        && prev.width === indicatorWidth
      ) {
        return prev;
      }
      return {
        left: centeredLeft,
        width: indicatorWidth,
        opacity: 1,
      };
    });
  }, [isWrapLayout, selectedKey, useSelectFallback]);

  // ----- EFFECTS ----- //

  useLayoutEffect(() => {
    const raf = requestAnimationFrame(() => {
      updateLayoutMode();
      updateIndicator();
    });
    return () => cancelAnimationFrame(raf);
  }, [tabs, selectedKey, showLabels, tabWidth, layout, updateIndicator, updateLayoutMode]);

  useEffect(() => {
    if (isWrapLayout || !wrapperRef.current) return undefined;

    const resizeObserver = new ResizeObserver(() => {
      updateLayoutMode();
      updateIndicator();
    });

    resizeObserver.observe(wrapperRef.current);
    if (measureRef.current) resizeObserver.observe(measureRef.current);
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [isWrapLayout, updateIndicator, updateLayoutMode]);

  useEffect(() => {
    if (isWrapLayout) return undefined;

    window.addEventListener('resize', updateLayoutMode);
    window.addEventListener('resize', updateIndicator);
    return () => {
      window.removeEventListener('resize', updateLayoutMode);
      window.removeEventListener('resize', updateIndicator);
    };
  }, [isWrapLayout, updateIndicator, updateLayoutMode]);

  // ----- HANDLERS ----- //

  const handleSelectFallbackChange = useCallback((_nextValue, option) => {
    const nextKey = option?.value;
    const matchedTab = (Array.isArray(tabs) ? tabs : []).find((tab) => tab.key === nextKey);
    matchedTab?.onClick?.();
  }, [tabs]);

  // ----- RENDER ----- //

  return (
    <div
      className={[
        'tabs-container',
        useSelectFallback ? 'tabs-container--select' : '',
        isWrapLayout ? 'tabs-container--wrap' : '',
      ].filter(Boolean).join(' ')}
      ref={wrapperRef}
    >
      {!isWrapLayout && (
        <div
          className="tabs-measure-container"
          ref={measureRef}
          aria-hidden="true"
        >
          {(Array.isArray(tabs) ? tabs : []).map((tab) => (
            <Tab
              key={`measure-${tab.key}`}
              tabKey={tab.key}
              label={tab.label}
              icon={tab.icon}
              iconKey={tab.iconKey}
              iconResource={tab.iconResource}
              isActive={false}
              rightContent={tab.rightContent}
              width={tabWidth}
              showLabel={showLabels}
              onClick={() => {}}
            />
          ))}
        </div>
      )}

      {!isWrapLayout && useSelectFallback ? (
        <SelectInput
          style={{
            background: "var(--panel)",
            borderWidth: 0,
            boxShadow: "none",
            fontSize: "15px",
            fontWeight: 500,
            width: 'auto',
          }}
          value={selectedKey || ''}
          options={selectOptions}
          placeholder="Select section..."
          hybrid={false}
          iconSize={20}
          width="auto"
          onValueChange={handleSelectFallbackChange}
        />
      ) : (

        <div className="tabs-track" ref={containerRef}>
          {(Array.isArray(tabs) ? tabs : []).map((tab) => (
            <Tab
              key={tab.key}
              tabKey={tab.key}
              label={tab.label}
              icon={tab.icon}
              iconKey={tab.iconKey}
              iconResource={tab.iconResource}
              isActive={tab.key === selectedKey}
              rightContent={tab.rightContent}
              width={tabWidth}
              showLabel={showLabels}
              tabRef={isWrapLayout ? undefined : ((node) => {
                if (!node) {
                  tabRefs.current.delete(tab.key);
                  return;
                }
                tabRefs.current.set(tab.key, node);
              })}
              onClick={tab.onClick}
            />
          ))}

          {endSlot ? (
            <div className="tabs-end-slot">
              {endSlot}
            </div>
          ) : null}

          {/* ACTIVE INDICATOR */}
          {!isWrapLayout && (
            <span
              className="tabs-active-indicator"
              style={{
                left: `${indicatorStyle.left}px`,
                opacity: indicatorStyle.opacity,
                width: `${indicatorStyle.width}px`,
              }}
            />
          )}
        </div>
      )}
    </div>
  );
});

// Dev Tools
Tabs.displayName = 'Tabs';

export default Tabs;
